import React, { useEffect, useMemo, useRef, useState } from "react"
import { useFormatProduct } from "@app/scripts/hooks/use-format-product"
import { wishlist, wishlistFilled } from "../hooks/icons"
import { ProductCardMedia } from "./product-card-media"
import {
  decodeHTMLEntities,
  isMobile,
  parseIfString,
  useProductBadge,
} from "@app/scripts/lib/utils"
import { ProductCardSwatches, Swatch } from "./product-card-swatches"
import { countryCurrency, SearchspringSearchResult, Variant } from "@app/types"
import { useBeaconResults } from "@app/scripts/providers/beacon-results"
import { BeaconProductResult } from "@app/scripts/lib/beacon"

type Props = Parameters<typeof useFormatProduct>[0] & {
  data: SearchspringSearchResult
  instantSearch?: boolean
  productCarousel?: boolean
  modelToggle?: boolean
  wishlistItemId?: string // when set, the wishlist icon will be active
  displayWishlistButton?: boolean
  isWishlistProductGrid?: boolean
  onWishlistClick?: (wishlistItemId?: string) => void
  extraBottomContent?: React.ReactNode
  swatchColours?: { name: string; hex: string }[]
  currentCountryCurrency?: countryCurrency
  useInteractiveSwatches?: boolean
}

export function CardProduct({
  data,
  instantSearch = false,
  productCarousel = false,
  modelToggle,
  wishlistItemId,
  displayWishlistButton = true,
  isWishlistProductGrid = false,
  onWishlistClick,
  extraBottomContent,
  swatchColours = [],
  currentCountryCurrency,
  useInteractiveSwatches = false,
}: Props) {
  const [hoveredSiblingHandle, setHoveredSiblingHandle] = useState<
    string | null
  >(null)

  const {
    handle,
    name,
    url,
    imageUrl,
    status,
    ss_instock_pct,
    images,
    variants,
    options,
    compareAtPrice,
    price,
    mfield_dotapparel21_siblings,
  } = data

  const hasCompareAtPrice =
    (Number(compareAtPrice) > 0 && Number(compareAtPrice) > Number(price)) || ""

  const available = status && ss_instock_pct > 1
  const productPhotos =
    (typeof images === "string" ? images.split("|") : images) ??
    (imageUrl ? [imageUrl] : [])

  const productVariantData = parseIfString(variants)
  const productSiblingData = parseIfString(mfield_dotapparel21_siblings)

  const publishedSiblings = useMemo(() => {
    return productSiblingData?.filter((item: Swatch) => item.published)
  }, [productSiblingData])

  const badge = React.useMemo(
    () => useProductBadge(data, available),
    [data, available]
  )

  const currentProductColour = useMemo(
    () =>
      parseIfString(options)
        .find(
          (option: { name: string; values: string[] }) =>
            option.name.toLowerCase() === "colour"
        )
        ?.values[0]?.replace(/\s+/g, "-")
        .toLowerCase() || "",
    [options]
  )

  const beacon = useBeaconResults()
  const containerRef = useRef<HTMLDivElement>(null)

  const beaconResult = useMemo<BeaconProductResult | null>(() => {
    // parentId = Shopify product_id, uid = Shopify variant_id.
    // Searchspring's search/category feed exposes:
    //   data.uid          -> Shopify product_id
    //   data.id           -> SS internal hash (NOT a Shopify id, never use)
    //   data.variants[0]  -> { id: variant_id, product_id, sku }
    // Recommendations expose data via mappings.core, where data.uid is also
    // the product_id and there is no data.id collision. If neither data.uid
    // nor a variant product_id is available, skip beacon attribution rather
    // than fall back to a Searchspring-internal id.
    const firstVariant = productVariantData?.[0] ?? data?.variants?.[0]
    const rawProductId = data?.uid ?? firstVariant?.product_id
    const rawVariantId = firstVariant?.id ?? data?.uid
    if (!rawProductId || !rawVariantId) return null
    const sku = firstVariant?.sku ?? data?.sku
    return {
      type: "product",
      parentId: String(rawProductId),
      uid: String(rawVariantId),
      ...(sku ? { sku: String(sku) } : {}),
    }
  }, [data, productVariantData])

  useEffect(() => {
    const el = containerRef.current
    if (!el || !beaconResult || !beacon.responseId) return
    beacon.observeProduct(el, beaconResult)
  }, [beacon, beaconResult])

  const activeUrl = useMemo(() => {
    if (!useInteractiveSwatches || !hoveredSiblingHandle) return url
    return `/products/${hoveredSiblingHandle}`
  }, [useInteractiveSwatches, hoveredSiblingHandle, url])

  return (
    <div
      ref={containerRef}
      className={`product-card ss-product-card--container ${instantSearch && "product-card--instant-search"}`}
    >
      <div className="product-card--featured-image product-card--photo-container">
        <div className="product-card--badge-swatches-container">
          <div
            className={`product-card--badges left top ${instantSearch && "is-hidden"} `}
          >
            {badge}
          </div>

          {!isMobile && !instantSearch && (
            <ProductCardSwatches
              mobile={false}
              swatches={publishedSiblings}
              currentHandle={handle}
              currentProductColour={currentProductColour}
              hoveredSiblingHandle={hoveredSiblingHandle}
              setHoveredSiblingHandle={setHoveredSiblingHandle}
              swatchColours={swatchColours}
              beaconResult={beaconResult}
            />
          )}
        </div>
        {displayWishlistButton && (
          <>
            {onWishlistClick ? (
              <button
                className="product-card__wishlist-icon product-card--wishlist"
                data-product-id={data?.uid || data?.id}
                data-variant-id={
                  productVariantData[0]?.id || data?.variants?.[0]?.id
                }
                onClick={
                  onWishlistClick
                    ? () => onWishlistClick?.(wishlistItemId)
                    : undefined
                }
              >
                {wishlistItemId ? wishlistFilled : wishlist}
              </button>
            ) : (
              <>
                <button
                  className="product-card__wishlist-icon product-card--wishlist product-card--wishlist"
                  data-product-id={data?.uid || data?.id}
                  data-variant-id={
                    productVariantData[0]?.id || data?.variants?.[0]?.id
                  }
                >
                  <span className="wishlist-icon-inactive"> {wishlist}</span>
                  <span className="wishlist-icon-active">{wishlistFilled}</span>
                </button>
              </>
            )}
          </>
        )}

        {!useInteractiveSwatches && productCarousel && isMobile ? (
          <img
            key={handle}
            src={`${productPhotos[0]}&width=600`}
            srcSet={`
       ${productPhotos[0]}&width=400 400w,
       ${productPhotos[0]}&width=600 600w
     `}
            alt={`Product Image`}
            className="product-card--photo-simple"
          />
        ) : (
          <ProductCardMedia
            media={productPhotos}
            instantSearch={instantSearch}
            modelToggle={modelToggle}
            draggable={useInteractiveSwatches ? !productCarousel : undefined}
            currentHandle={handle}
            productSiblingData={publishedSiblings}
            hoveredSiblingHandle={hoveredSiblingHandle}
            activeUrl={useInteractiveSwatches ? activeUrl : undefined}
            beaconResult={beaconResult ?? undefined}
          />
        )}
      </div>
      <div className="product-card-info product-card--information-container">
        <div className="product-card__main-details-container flex-container product-card--initial--info-container">
          <a
            href={activeUrl}
            onClick={() => {
              if (beaconResult) beacon.reportClickthrough(beaconResult)
            }}
          >
            <div className="product-card--title">
              {decodeHTMLEntities(name)?.toLowerCase()}
            </div>
          </a>
          <div
            className={`price ${instantSearch ? "is-hidden" : "is-hidden md:is-visible"}  product-card--price`}
          >
            {price > 0 &&
              `${currentCountryCurrency?.currencySymbol}${price}${
                currentCountryCurrency?.countryCode !== "AU"
                  ? ` ${currentCountryCurrency?.currencyCode}`
                  : ""
              }`}
          </div>
        </div>

        {isMobile || instantSearch ? (
          <div className="product-card-price--container flex-container">
            {price > 0 && (
              <p className="price product-card--price">
                {currentCountryCurrency?.currencySymbol}
                {price}
                {currentCountryCurrency?.countryCode !== "AU" &&
                  ` ${currentCountryCurrency?.currencyCode}`}
              </p>
            )}

            {hasCompareAtPrice && (
              <div
                className={`price ${!instantSearch ? "" : "is-hidden"}  product-card--sale-price`}
              >
                {currentCountryCurrency?.currencySymbol}
                {compareAtPrice}
                {currentCountryCurrency?.countryCode !== "AU" &&
                  ` ${currentCountryCurrency?.currencyCode}`}
              </div>
            )}
          </div>
        ) : null}

        <div
          className={`product-card__secondary-details-container product-card--secondary-info-container ${instantSearch || isWishlistProductGrid ? "is-hidden" : ""}`}
        >
          <div className="product-card__secondary-details-container-items">
            <div
              className={`product-card__sizes-simple ${instantSearch ? "is-hidden " : ""} product-card--sizes`}
            >
              {productVariantData.length > 0 &&
                data?.handle != "gift-card" &&
                productVariantData.map(
                  (size: Variant) =>
                    size.option2 && (
                      <div
                        key={size.id}
                        className={`product-card__size ${(size as Variant & { inventory_quantity?: number }).inventory_quantity === 0 && "out-of-stock"}`}
                      >
                        <span>
                          {size.option2 === "-" ? "One Size" : size.option2}
                        </span>
                      </div>
                    )
                )}
            </div>
            {hasCompareAtPrice && (
              <div
                className={`price ${instantSearch || !isMobile ? "" : "is-hidden"}  product-card--sale-price`}
              >
                {currentCountryCurrency?.currencySymbol}
                {compareAtPrice}
                {currentCountryCurrency?.countryCode !== "AU" &&
                  ` ${currentCountryCurrency?.currencyCode}`}
              </div>
            )}
          </div>
        </div>
        {isMobile || instantSearch ? (
          <ProductCardSwatches
            mobile={true}
            useInteractiveSwatches={useInteractiveSwatches}
            swatches={publishedSiblings}
            currentHandle={handle}
            currentProductColour={currentProductColour}
            hoveredSiblingHandle={hoveredSiblingHandle}
            setHoveredSiblingHandle={setHoveredSiblingHandle}
            swatchColours={swatchColours}
            beaconResult={beaconResult}
          />
        ) : null}
      </div>

      {extraBottomContent}
    </div>
  )
}
